import { KeycloakOptions, KeycloakService } from 'keycloak-angular';
import { ConfigurationService } from '@smals/ngx-configuration-service';
import { UserService } from '../services/user/user.service';
import { LocalStorageService } from '../services/local-storage/local-storage.service';
import * as Sentry from '@sentry/angular';
import { APP_VERSION } from '../app-version';
import type { ErrorEvent, EventHint } from '@sentry/core/build/types/types-hoist/event';

export function initializer(
  keycloak: KeycloakService, configurationService: ConfigurationService, userService: UserService, localStorageService: LocalStorageService): () => Promise<void> {
  return async (): Promise<void> => {

    try {
      await keycloakInit(keycloak, configurationService);
      await userTokenInit(userService, localStorageService);
      await sentryInit(configurationService, localStorageService);
    } catch (error) {
      console.error('Error during initialization:', error);
      userService.openHasNoAccessDialog();
      throw error;
    }
  };
}

async function keycloakInit(keycloak: KeycloakService, configurationService: ConfigurationService): Promise<void> {
  const config: KeycloakOptions = {
    config: {
      url: configurationService.getEnvironmentVariable('keycloakAuthServerUrl'),
      realm: configurationService.getEnvironmentVariable('keycloakRealm'),
      clientId: configurationService.getEnvironmentVariable('keycloakClientId')
    },
    initOptions: {
      onLoad: 'login-required',
      checkLoginIframe: false
    },
    bearerExcludedUrls: ['/assets'],
    enableBearerInterceptor: true,
    loadUserProfileAtStartUp: false
  };

  await keycloak.init(config);
}

async function userTokenInit(userService: UserService, localStorageService: LocalStorageService): Promise<void> {
  const ssin = localStorageService.getItemWithExpiracy('patientSsin');
  await userService.initializeUserToken(ssin);
}

async function sentryInit(configurationService: ConfigurationService, localStorageService: LocalStorageService): Promise<void> {
  Sentry.init({
    dsn: 'https://2bf92f9955924818848b605bea313d13@o452936.ingest.us.sentry.io/6041289',
    environment: configurationService.getEnvironment(),
    //uncomment to enable span usage
    // tracesSampleRate: 0.1,
    sendDefaultPii: false,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    release: `vidis-ui-${APP_VERSION.version}`,
    integrations: [
      //uncomment to enable span usage
      //Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        networkDetailAllowUrls: [
          /^https:\/\/vidis-api\.int\.pub\.vascloud\.be\/REST\/vidis\/.*$/,
          /^https:\/\/vidis-api\.acc\.vasha\.be\/REST\/vidis\/.*$/,
          /^https:\/\/vidis-api\.vasha\.be\/REST\/vidis\/.*$/],
        networkCaptureBodies: false,
        networkResponseHeaders: ['TRACE-ID'],
        networkRequestHeaders: ['user-type', 'client-type'],
        maskAllText: false,
        blockAllMedia: false,
      })],
    tracePropagationTargets: [configurationService.getEnvironmentVariable('apiUrl')],
    beforeSend(event: ErrorEvent, hint: EventHint) {
      localStorageService.setItemWithExpiracy('sentryEventId', event.event_id, 3600000);
      for (const key of Object.keys(event)) {
        if (typeof event[key] === 'string' || event[key] instanceof String) {
          event[key] = event[key].replace(/ssin=.{11}/ig, 'ssin=***********');
          event[key] = event[key].replace(/BEP.{9}/ig, ':ridcode');
        }
      }

      if (event.request?.url) {
        event.request.url = event.request.url.replace(/BEP.{9}/ig, ':ridcode');
      }

      if (event.user) {
        delete event.user.ip_address;
      }

      if (event.exception?.values) {
        event.exception.values = event.exception.values.map(value => {
          return {
            ...value,
            value: value.value?.replace(/BEP.{9}/ig, ':ridcode')
          };
        });
      }

      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
          if (breadcrumb.data) {
            let newData = {};
            for (const key of Object.keys(breadcrumb.data)) {
              if (typeof breadcrumb.data[key] === 'string' || breadcrumb.data[key] instanceof String) {
                newData[key] = breadcrumb.data[key]
                  .replace(/ssin=.{11}/ig, 'ssin=***********')
                  .replace(/BEP.{9}/ig, ':ridcode');
              }
            }
            return {
              ...breadcrumb,
              data: {
                ...breadcrumb.data,
                ...newData
              }
            };
          }

          return breadcrumb;
        });
      }

      return event;
    }
  });
}
